import { useMutation, useQueryClient } from "@tanstack/react-query";
import { produce } from "immer";

import type { ChatMessageI } from "shared/ui/messages/types";

import { useUser } from "@/hooks/use-global-context";
import { GetChatInboxResponseI } from "@/api/routes/messaging";
import { getChatIdentity } from "@/modules/messages/utils";
import { messagingSocketEventHandler } from "@/lib/websockets/messaging/event-handlers";

import { MessagingQueryKeys } from "./keys";

export const useMutationSendMessage = (
  campaignId: string | undefined,
  customerId: string | undefined
) => {
  const me = useUser();

  return useMutation({
    mutationFn: async (newMessageContent: string): Promise<ChatMessageI> => {
      messagingSocketEventHandler.enterChatV2(
        campaignId as string,
        customerId as string,
        me?.id as string
      );
      messagingSocketEventHandler.sendMessage(newMessageContent);

      return {
        sender_id: me?.id,
        message: newMessageContent,
        timestamp: new Date().toISOString(),
      };
    },
  });
};

export const useMutationResetUnreadMessagesCount = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (chatId: string | null) => {
      queryClient.setQueryData(MessagingQueryKeys.chats(""), (old: any) => {
        // The key is hardcoded to the unfiltered inbox, so there may be no
        // cached entry to update at all - filtered inbox, or gc'd.
        if (!old?.pages) {
          return old;
        }

        const newPages = [...old.pages];

        const modifiedNewPages = produce(
          newPages,
          (draft: GetChatInboxResponseI[]) => {
            for (const page of draft) {
              const chatToModify = page.chat_inbox?.find(
                (chat) => getChatIdentity(chat) === chatId
              );

              if (chatToModify && chatToModify.notification_count > 0) {
                console.debug(
                  "[CHAT] Unread messages counter have been reset",
                  getChatIdentity(chatToModify)
                );

                chatToModify.notification_count = 0;
                break;
              }
            }
          }
        );

        return { ...old, pages: modifiedNewPages };
      });

      return Promise.resolve(true);
    },
  });
};
